import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import React, {useEffect, useMemo, useState} from 'react';
import {LanguagesController, SupportedLanguage} from '../../api/translations/languages.controller';
import {TranslationEntry, TranslationsController} from '../../api/translations/translations.controller';

export const TranslationsPage = () => {
  const [entries, setEntries] = useState<TranslationEntry[]>([]);
  const [languages, setLanguages] = useState<SupportedLanguage[]>([]);
  const [filterLanguage, setFilterLanguage] = useState<string>('');
  const [search, setSearch] = useState('');

  const [key, setKey] = useState('');
  const [formLanguage, setFormLanguage] = useState<string>('');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const [langDialogOpen, setLangDialogOpen] = useState(false);
  const [deleteLangTarget, setDeleteLangTarget] = useState<string | null>(null);
  const [deleteEntryTarget, setDeleteEntryTarget] = useState<string | null>(null);
  const [newCode, setNewCode] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const refreshTranslations = async () => {
    const data = await TranslationsController.getTranslations();
    setEntries(data);
    return data;
  };
  const refreshLanguages = async () => {
    const langs = await LanguagesController.getLanguages();
    setLanguages(langs);
    setFormLanguage(prev => prev || langs[0]?.code || '');
    return langs;
  };

  useEffect(() => {
    const load = async () => {
      const [langs, allEntries] = await Promise.all([refreshLanguages(), refreshTranslations()]);
      setFilterLanguage(prev => {
        if (prev) return prev;
        const withEntries = langs.find(l => allEntries.some(e => e.language === l.code));
        return (withEntries ?? langs[0])?.code ?? '';
      });
    };
    load();
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries
      .filter(e => e.language === filterLanguage)
      .filter(e => q === '' || e.key.toLowerCase().includes(q) || e.value.toLowerCase().includes(q))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [entries, filterLanguage, search]);

  const canSave = key.trim() !== '' && formLanguage.trim() !== '' && value.trim() !== '' && !saving;

  const save = async () => {
    setSaving(true);
    try {
      await TranslationsController.upsertTranslation({key: key.trim(), language: formLanguage, value});
      setKey('');
      setValue('');
      await refreshTranslations();
    } finally {
      setSaving(false);
    }
  };

  const addLanguage = async () => {
    await LanguagesController.upsertLanguage({code: newCode.trim(), label: newLabel.trim()});
    setNewCode('');
    setNewLabel('');
    setLangDialogOpen(false);
    await refreshLanguages();
  };

  const confirmRemoveLanguage = async () => {
    if (deleteLangTarget === null) return;
    const code = deleteLangTarget;
    await LanguagesController.deleteLanguage(code);
    setDeleteLangTarget(null);
    if (filterLanguage === code) setFilterLanguage('');
    await refreshLanguages();
    await refreshTranslations();
  };

  const edit = (entry: TranslationEntry) => {
    setKey(entry.key);
    setFormLanguage(entry.language);
    setValue(entry.value);
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const remove = async () => {
    if (!deleteEntryTarget) return;
    await TranslationsController.deleteTranslation(deleteEntryTarget);
    setDeleteEntryTarget(null);
    await refreshTranslations();
  };

  const languageSelect = (label: string, val: string, onChange: (v: string) => void) => (
    <FormControl size="small" sx={{minWidth: 180}}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={val} onChange={e => onChange(e.target.value)}>
        {languages.map(l => <MenuItem key={l.code} value={l.code}>{l.label} ({l.code})</MenuItem>)}
      </Select>
    </FormControl>
  );

  const canAddLanguage = newCode.trim() !== '' && newLabel.trim() !== ''
    && !languages.some(l => l.code === newCode.trim());

  return <Box sx={{p: 3}}>
    <Typography variant="h5" sx={{mb: 2}}>Tłumaczenia</Typography>

    {/* Sekcja: wspierane jezyki */}
    <Paper sx={{p: 2, mb: 3}}>
      <Typography variant="subtitle2" sx={{mb: 1}}>Wspierane języki</Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {languages.map(l => (
          <Chip key={l.code} label={`${l.label} (${l.code})`} onDelete={() => setDeleteLangTarget(l.code)} size="small"/>
        ))}
        {languages.length === 0 && <Typography variant="body2" color="text.secondary">Brak języków — dodaj pierwszy.</Typography>}
        <Button variant="outlined" size="small" startIcon={<AddIcon/>} onClick={() => setLangDialogOpen(true)} sx={{ml: 1}}>
          Dodaj nowy język
        </Button>
      </Stack>
    </Paper>

    {/* Formularz dodawania / edycji tlumaczenia */}
    <Paper sx={{p: 2, mb: 3}}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField label="Klucz" value={key} onChange={e => setKey(e.target.value)} size="small" sx={{flex: 1}}/>
          {languageSelect('Język', formLanguage, setFormLanguage)}
        </Stack>
        <TextField label="Tłumaczenie" value={value} onChange={e => setValue(e.target.value)} multiline minRows={3} fullWidth/>
        <Box>
          <Button variant="contained" disabled={!canSave} onClick={save}>Zapisz</Button>
          <Typography variant="caption" color="text.secondary" sx={{ml: 2}}>
            Zapis po (klucz, język). Klik w kafelek wczytuje go do edycji.
          </Typography>
        </Box>
      </Stack>
    </Paper>

    {/* Filtr jezyka + szukajka */}
    <Stack direction="row" spacing={2} sx={{mb: 2}} alignItems="center">
      {languageSelect('Pokaż język', filterLanguage, setFilterLanguage)}
      <TextField
        placeholder="Szukaj po kluczu lub tekście tłumaczenia"
        value={search}
        onChange={e => setSearch(e.target.value)}
        size="small"
        sx={{flex: 1}}
        InputProps={{startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>}}
      />
      <Typography variant="body2" color="text.secondary">{visible.length}</Typography>
    </Stack>

    {/* Widok jednego jezyka, 2 kolumny */}
    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2}}>
      {visible.map(entry => (
        <Paper key={entry.id} variant="outlined" sx={{p: 1.5, cursor: 'pointer'}} onClick={() => edit(entry)}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box sx={{minWidth: 0}}>
              <Typography variant="caption" sx={{fontFamily: 'monospace', color: 'text.secondary', wordBreak: 'break-all'}}>{entry.key}</Typography>
              <Typography variant="body2" sx={{whiteSpace: 'pre-wrap', mt: 0.5}}>{entry.value}</Typography>
            </Box>
            <IconButton size="small" aria-label="Usuń" onClick={e => {e.stopPropagation(); setDeleteEntryTarget(entry.id);}}>
              <DeleteOutlineIcon fontSize="small"/>
            </IconButton>
          </Stack>
        </Paper>
      ))}
    </Box>
    {visible.length === 0 && (
      <Typography variant="body2" color="text.secondary" sx={{p: 2}}>Brak tłumaczeń dla wybranego języka/frazy.</Typography>
    )}

    <Dialog open={deleteLangTarget !== null} onClose={() => setDeleteLangTarget(null)} maxWidth="xs" fullWidth>
      <DialogTitle>Usunąć język?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Usuniesz język <b>{deleteLangTarget}</b> oraz{' '}
          <b>{entries.filter(e => e.language === deleteLangTarget).length}</b> jego tłumaczeń. Tej operacji nie cofniesz.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteLangTarget(null)}>Anuluj</Button>
        <Button variant="contained" color="error" onClick={confirmRemoveLanguage}>Usuń</Button>
      </DialogActions>
    </Dialog>
    <Dialog open={deleteEntryTarget !== null} onClose={() => setDeleteEntryTarget(null)} maxWidth="xs" fullWidth>
      <DialogTitle>Usunąć tłumaczenie?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Tej operacji nie cofniesz.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteEntryTarget(null)}>Anuluj</Button>
        <Button variant="contained" color="error" onClick={remove}>Usuń</Button>
      </DialogActions>
    </Dialog>
        <Dialog open={langDialogOpen} onClose={() => setLangDialogOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Dodaj nowy język</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{mt: 1}}>
          <TextField label="Kod (np. de)" value={newCode} onChange={e => setNewCode(e.target.value)} size="small" fullWidth autoFocus/>
          <TextField label="Nazwa (np. Deutsch)" value={newLabel} onChange={e => setNewLabel(e.target.value)} size="small" fullWidth/>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setLangDialogOpen(false)}>Anuluj</Button>
        <Button variant="contained" disabled={!canAddLanguage} onClick={addLanguage}>Dodaj</Button>
      </DialogActions>
    </Dialog>
  </Box>;
};
